import React from "react";
import { BrandConfigByCode } from "@constants/common";

import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { BrandCode } from "@typed/common";
import { GacDesktopNavbarItem, NavbarSection } from "@components/Catalyst/navbar";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "@components/Catalyst/dropdown";
import { useAuth } from "@contexts/AuthContext";
import { FaCaretDown, FaUser } from "react-icons/fa";
import toast from "react-hot-toast";
import DropMenuItem, { ExpansionMenuItem } from "./DropMenuItem";
import { LinkEntry, getBrandedLinks, mainLinks, saLinks } from "../data";

export type MenuData = {
  brandCode: BrandCode;
  brandedLinks: LinkEntry[];
  gacLinks: LinkEntry[];
};

export const DesktopNavbar = ({ brandCode, handleSearch }: { brandCode: BrandCode; handleSearch: () => void }) => {
  const { gacUser, logout, ShowLoginModal } = useAuth();

  const brandedLinks = brandCode === "gac" ? [] : [...getBrandedLinks(brandCode)];
  const gacLinks = brandCode === "gac" ? [...getBrandedLinks(brandCode), ...mainLinks] : [...mainLinks];

  const { brandLogo, bgColor, logoAlt, url } = BrandConfigByCode[brandCode];

  return (
    <header
      className="dark hidden w-full items-center lg:flex"
      style={{
        background: `linear-gradient(122.25deg, ${bgColor} calc(100% - 580px), #000000 calc(100% - 580px))`,
      }}
    >
      <div className="flex-1">
        <a href={url}>
          <img src={brandLogo} alt={logoAlt} className="h-14" />
        </a>
      </div>

      <NavbarSection className="dark">
        {brandedLinks?.length ? (
          <div>
            {brandedLinks
              .filter((l) => l.desktopSort)
              .sort((l1, l2) => (l1.desktopSort ?? 0) - (l2.desktopSort ?? 0))
              .map((item) => (
                <DropMenuItem item={item} brandCode={item.brandCode ?? "gac"} key={item.label} />
              ))}
          </div>
        ) : null}

        {gacLinks
          .filter((l) => l.desktopSort)
          .sort((l1, l2) => (l1.desktopSort ?? 0) - (l2.desktopSort ?? 0))
          .map((item) => (
            <DropMenuItem item={item} key={item.label} brandCode={item.brandCode ?? "gac"} />
          ))}

        <GacDesktopNavbarItem href="/store-locator">
          Find a Store
          <MapPinIcon />
        </GacDesktopNavbarItem>
        <GacDesktopNavbarItem onClick={handleSearch}>
          <MagnifyingGlassIcon />
        </GacDesktopNavbarItem>

        <Dropdown>
          <DropdownButton as={GacDesktopNavbarItem} aria-label="Open Studio Assistant Menu">
            <img className="h-7 w-7" width={28} height={28} src="/images/logos/Stud_grn.svg" alt="Studio Assistant" />
            <FaCaretDown size={18} />
          </DropdownButton>
          <DropdownMenu className="dark bg-black">
            {!gacUser && (
              <>
                <DropdownItem onClick={() => ShowLoginModal("login")}>
                  <DropdownLabel>Sign In</DropdownLabel>
                </DropdownItem>

                <DropdownItem onClick={() => ShowLoginModal("forgot-password")}>
                  <DropdownLabel> Forgot Password</DropdownLabel>
                </DropdownItem>

                <DropdownItem onClick={() => ShowLoginModal("new-account")}>
                  <DropdownLabel>Create Account </DropdownLabel>
                </DropdownItem>
              </>
            )}
            {gacUser && (
              <>
                <DropdownItem href="/studio-assistant/profile">
                  <DropdownLabel>
                    <div className="flex items-center">
                      <FaUser size={16} />

                      {gacUser.email}
                    </div>
                  </DropdownLabel>
                </DropdownItem>

                {saLinks.map((childItem) => (
                  <ExpansionMenuItem item={childItem} brandCode="gac" key={childItem.label} />
                ))}

                <DropdownItem
                  onClick={() => {
                    toast.promise(logout(), {
                      loading: "Signing out...",
                      success: "You have signed out.",
                      error: "An error occurred while signing out.",
                    });
                  }}
                >
                  <DropdownLabel>Sign Out</DropdownLabel>
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </Dropdown>
      </NavbarSection>
    </header>
  );
};
